import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Label,
  Message,
  Modal,
  Pagination,
  Popup,
  Table,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';

import { ITEMS_PER_PAGE } from '../constants';
import { renderQuota } from '../helpers/render';

function renderTimestamp(timestamp) {
  return <>{timestamp2string(timestamp)}</>;
}

function renderStatus(status, t) {
  switch (status) {
    case 1:
      return (
        <Label basic color='green'>
          {t('token.table.status_enabled')}
        </Label>
      );
    case 2:
      return (
        <Label basic color='red'>
          {t('token.table.status_disabled')}
        </Label>
      );
    case 3:
      return (
        <Label basic color='yellow'>
          {t('token.table.status_expired')}
        </Label>
      );
    case 4:
      return (
        <Label basic color='grey'>
          {t('token.table.status_depleted')}
        </Label>
      );
    default:
      return (
        <Label basic color='black'>
          {t('token.table.status_unknown')}
        </Label>
      );
  }
}

const TokensTable = () => {
  const { t } = useTranslation();

  const COPY_OPTIONS = [
    { key: 'raw', text: t('token.copy_options.raw'), value: '' },
    { key: 'next', text: t('token.copy_options.next'), value: 'next' },
    { key: 'ama', text: t('token.copy_options.ama'), value: 'ama' },
    { key: 'opencat', text: t('token.copy_options.opencat'), value: 'opencat' },
    { key: 'lobe', text: t('token.copy_options.lobe'), value: 'lobechat' },
  ];

  const OPEN_LINK_OPTIONS = [
    { key: 'next', text: t('token.copy_options.next'), value: 'next' },
    { key: 'ama', text: t('token.copy_options.ama'), value: 'ama' },
    { key: 'opencat', text: t('token.copy_options.opencat'), value: 'opencat' },
    { key: 'lobe', text: t('token.copy_options.lobe'), value: 'lobechat' },
  ];

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [targetTokenIdx, setTargetTokenIdx] = useState(0);
  const [orderBy, setOrderBy] = useState('');
  const [usageToken, setUsageToken] = useState(null);
  const [chatToken, setChatToken] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatResult, setChatResult] = useState(null);
  const [chatForm, setChatForm] = useState({
    model: 'deepseek-ai/DeepSeek-V4-Pro',
    systemPrompt: '你是一个简洁、准确的 API 连通性测试助手。',
    userPrompt: '请用一句话回复：当前 API 调用正常。',
    maxTokens: 512,
    temperature: 0.7,
    stream: false,
  });

  const getServerAddress = () => {
    let status = localStorage.getItem('status');
    let serverAddress = '';
    if (status) {
      status = JSON.parse(status);
      serverAddress = status.server_address;
    }
    const currentHost = window.location.hostname;
    const localServerHosts = ['localhost', '127.0.0.1', '0.0.0.0', '::1'];
    let shouldUseCurrentOrigin = serverAddress === '';

    try {
      const configuredUrl = new URL(serverAddress);
      shouldUseCurrentOrigin =
        shouldUseCurrentOrigin ||
        (localServerHosts.includes(configuredUrl.hostname) &&
          !localServerHosts.includes(currentHost));
    } catch (e) {
      shouldUseCurrentOrigin = true;
    }

    if (shouldUseCurrentOrigin) {
      serverAddress = window.location.origin;
    }
    return serverAddress.replace(/\/$/, '');
  };

  const getTokenSecret = (token) => `sk-${token.key}`;

  const getApiBaseURL = () => `${getServerAddress()}/v1`;

  const getDefaultChatModel = (token) => {
    if (token?.models) {
      const models = token.models
        .split(',')
        .map((model) => model.trim())
        .filter(Boolean);
      if (models.length > 0) return models[0];
    }
    return 'deepseek-ai/DeepSeek-V4-Pro';
  };

  const extractAssistantText = (responseText, stream) => {
    if (!responseText) return '';
    if (!stream) {
      try {
        const data = JSON.parse(responseText);
        return (
          data?.choices?.[0]?.message?.content ||
          data?.choices?.[0]?.text ||
          data?.error?.message ||
          responseText
        );
      } catch (e) {
        return responseText;
      }
    }

    const lines = responseText.split('\n');
    let content = '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const payload = trimmed.replace(/^data:\s*/, '');
      if (payload === '[DONE]') continue;
      try {
        const data = JSON.parse(payload);
        content +=
          data?.choices?.[0]?.delta?.content ||
          data?.choices?.[0]?.text ||
          '';
      } catch (e) {
        // Keep parsing the following SSE chunks.
      }
    }
    return content || responseText;
  };

  const extractErrorText = (responseText) => {
    if (!responseText) return '';
    try {
      const data = JSON.parse(responseText);
      if (typeof data?.error === 'string') return data.error;
      return (
        data?.error?.message ||
        data?.message ||
        data?.msg ||
        data?.detail ||
        ''
      );
    } catch (e) {
      return responseText;
    }
  };

  const openChatTester = (token) => {
    setChatToken(token);
    setChatResult(null);
    setChatForm((current) => ({
      ...current,
      model: getDefaultChatModel(token),
    }));
  };

  const copyUsageText = async (text) => {
    if (await copy(text)) {
      showSuccess(t('token.messages.copy_success'));
    } else {
      showWarning(t('token.messages.copy_failed'));
      setSearchKeyword(text);
    }
  };

  const loadTokens = async (startIdx) => {
    const res = await API.get(`/api/token/?p=${startIdx}&order=${orderBy}`);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setTokens(data);
      } else {
        let newTokens = [...tokens];
        newTokens.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
        setTokens(newTokens);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(tokens.length / ITEMS_PER_PAGE) + 1) {
        // In this case we have to load more data and then append them.
        await loadTokens(activePage - 1, orderBy);
      }
      setActivePage(activePage);
    })();
  };

  const refresh = async () => {
    setLoading(true);
    await loadTokens(activePage - 1);
  };

  const onCopy = async (type, key) => {
    const serverAddress = getServerAddress();
    let encodedServerAddress = encodeURIComponent(serverAddress);
    const nextLink = localStorage.getItem('chat_link');
    let nextUrl;

    if (nextLink) {
      nextUrl =
        nextLink + `/#/?settings={"key":"sk-${key}","url":"${serverAddress}"}`;
    } else {
      nextUrl = `https://app.nextchat.dev/#/?settings={"key":"sk-${key}","url":"${serverAddress}"}`;
    }

    let url;
    switch (type) {
      case 'ama':
        url = `ama://set-api-key?server=${encodedServerAddress}&key=sk-${key}`;
        break;
      case 'opencat':
        url = `opencat://team/join?domain=${encodedServerAddress}&token=sk-${key}`;
        break;
      case 'next':
        url = nextUrl;
        break;
      case 'lobechat':
        url =
          nextLink +
          `/?settings={"keyVaults":{"openai":{"apiKey":"sk-${key}","baseURL":"${serverAddress}/v1"}}}`;
        break;
      default:
        url = `sk-${key}`;
    }
    if (await copy(url)) {
      showSuccess(t('token.messages.copy_success'));
    } else {
      showWarning(t('token.messages.copy_failed'));
      setSearchKeyword(url);
    }
  };

  const onOpenLink = async (type, key) => {
    const serverAddress = getServerAddress();
    let encodedServerAddress = encodeURIComponent(serverAddress);
    const chatLink = localStorage.getItem('chat_link');
    let defaultUrl;

    if (chatLink) {
      defaultUrl =
        chatLink + `/#/?settings={"key":"sk-${key}","url":"${serverAddress}"}`;
    } else {
      defaultUrl = `https://app.nextchat.dev/#/?settings={"key":"sk-${key}","url":"${serverAddress}"}`;
    }
    let url;
    switch (type) {
      case 'ama':
        url = `ama://set-api-key?server=${encodedServerAddress}&key=sk-${key}`;
        break;

      case 'opencat':
        url = `opencat://team/join?domain=${encodedServerAddress}&token=sk-${key}`;
        break;

      case 'lobechat':
        url =
          chatLink +
          `/?settings={"keyVaults":{"openai":{"apiKey":"sk-${key}","baseURL":"${serverAddress}/v1"}}}`;
        break;

      default:
        url = defaultUrl;
    }

    window.open(url, '_blank');
  };

  useEffect(() => {
    loadTokens(0, orderBy)
      .then()
      .catch((reason) => {
        showError(reason);
      });
  }, [orderBy]);

  const manageToken = async (id, action, idx) => {
    let data = { id };
    let res;
    switch (action) {
      case 'delete':
        res = await API.delete(`/api/token/${id}/`);
        break;
      case 'enable':
        data.status = 1;
        res = await API.put('/api/token/?status_only=true', data);
        break;
      case 'disable':
        data.status = 2;
        res = await API.put('/api/token/?status_only=true', data);
        break;
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('token.messages.operation_success'));
      let token = res.data.data;
      let newTokens = [...tokens];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      if (action === 'delete') {
        newTokens[realIdx].deleted = true;
      } else {
        newTokens[realIdx].status = token.status;
      }
      setTokens(newTokens);
    } else {
      showError(message);
    }
  };

  const searchTokens = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadTokens(0);
      setActivePage(1);
      setOrderBy('');
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/token/search?keyword=${searchKeyword}`);
    const { success, message, data } = res.data;
    if (success) {
      setTokens(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortToken = (key) => {
    if (tokens.length === 0) return;
    setLoading(true);
    let sortedTokens = [...tokens];
    sortedTokens.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedTokens[0].id === tokens[0].id) {
      sortedTokens.reverse();
    }
    setTokens(sortedTokens);
    setLoading(false);
  };

  const handleOrderByChange = (e, { value }) => {
    setOrderBy(value);
    setActivePage(1);
  };

  const renderCodeBlock = (text) => (
    <pre
      style={{
        background: '#f7f7f8',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        color: '#1f2937',
        fontSize: 13,
        lineHeight: 1.55,
        margin: '8px 0 12px',
        overflowX: 'auto',
        padding: '12px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {text}
    </pre>
  );

  const renderCopyButton = (text) => (
    <Button size='tiny' onClick={() => copyUsageText(text)}>
      复制
    </Button>
  );

  const renderUsageModal = () => {
    if (!usageToken) return null;

    const apiKey = getTokenSecret(usageToken);
    const baseURL = getApiBaseURL();
    const windowsSession = `$env:OPENAI_API_KEY="${apiKey}"\n$env:OPENAI_BASE_URL="${baseURL}"`;
    const windowsPersist = `setx OPENAI_API_KEY "${apiKey}"\nsetx OPENAI_BASE_URL "${baseURL}"`;
    const macSession = `export OPENAI_API_KEY="${apiKey}"\nexport OPENAI_BASE_URL="${baseURL}"`;
    const macPersist = `echo 'export OPENAI_API_KEY="${apiKey}"' >> ~/.zshrc\necho 'export OPENAI_BASE_URL="${baseURL}"' >> ~/.zshrc\nsource ~/.zshrc`;
    const curlExample = `curl ${baseURL}/chat/completions \\\n  -H "Authorization: Bearer ${apiKey}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"deepseek-ai/DeepSeek-V4-Pro","messages":[{"role":"user","content":"你好"}],"stream":true,"max_tokens":512}'`;

    return (
      <Modal
        open={!!usageToken}
        onClose={() => setUsageToken(null)}
        size='large'
        closeIcon
      >
        <Modal.Header>令牌使用方法</Modal.Header>
        <Modal.Content scrolling>
          <Message info>
            这个令牌兼容 OpenAI API。应用里把 API Key 配成下面的密钥，把 Base URL 配成下面的地址即可。
          </Message>

          <h4>基础配置</h4>
          <div style={{ marginBottom: 8 }}>
            <strong>API Key</strong>
            {renderCopyButton(apiKey)}
          </div>
          {renderCodeBlock(apiKey)}
          <div style={{ marginBottom: 8 }}>
            <strong>Base URL</strong>
            {renderCopyButton(baseURL)}
          </div>
          {renderCodeBlock(baseURL)}

          <h4>Windows PowerShell 临时配置</h4>
          <p>只在当前 PowerShell 窗口生效，适合临时测试。</p>
          {renderCopyButton(windowsSession)}
          {renderCodeBlock(windowsSession)}

          <h4>Windows 长期配置</h4>
          <p>执行后重新打开 PowerShell 或命令行窗口生效。</p>
          {renderCopyButton(windowsPersist)}
          {renderCodeBlock(windowsPersist)}

          <h4>macOS 临时配置</h4>
          <p>只在当前终端窗口生效。</p>
          {renderCopyButton(macSession)}
          {renderCodeBlock(macSession)}

          <h4>macOS 长期配置</h4>
          <p>默认写入 zsh 配置。使用 bash 时请把 ~/.zshrc 改成 ~/.bash_profile。</p>
          {renderCopyButton(macPersist)}
          {renderCodeBlock(macPersist)}

          <h4>最小调用示例</h4>
          {renderCopyButton(curlExample)}
          {renderCodeBlock(curlExample)}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setUsageToken(null)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    );
  };

  const runChatTest = async () => {
    if (!chatToken) return;

    const startedAt = performance.now();
    const requestBody = {
      model: chatForm.model.trim(),
      messages: [
        {
          role: 'system',
          content: chatForm.systemPrompt,
        },
        {
          role: 'user',
          content: chatForm.userPrompt,
        },
      ],
      max_tokens: Number(chatForm.maxTokens) || 512,
      temperature: Number(chatForm.temperature),
      stream: chatForm.stream,
    };

    setChatLoading(true);
    setChatResult({
      requestBody,
      responseText: '',
      assistantText: '',
      status: '',
      durationMs: 0,
      error: '',
    });

    try {
      const response = await fetch(`${getApiBaseURL()}/chat/completions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getTokenSecret(chatToken)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      const responseText = await response.text();
      const durationMs = Math.round(performance.now() - startedAt);
      let prettyResponse = responseText;

      try {
        prettyResponse = JSON.stringify(JSON.parse(responseText), null, 2);
      } catch (e) {
        // Keep raw SSE or provider error text unchanged.
      }
      const assistantText = extractAssistantText(responseText, chatForm.stream);
      const errorText = response.ok
        ? ''
        : extractErrorText(responseText) ||
          `请求已返回非 2xx 状态：${response.status} ${response.statusText}`;

      setChatResult({
        requestBody,
        responseText: prettyResponse,
        assistantText,
        status: `${response.status} ${response.statusText}`,
        durationMs,
        error: errorText,
      });
    } catch (error) {
      setChatResult({
        requestBody,
        responseText: '',
        assistantText: '',
        status: 'NETWORK_ERROR',
        durationMs: Math.round(performance.now() - startedAt),
        error: error.message || String(error),
      });
    } finally {
      setChatLoading(false);
    }
  };

  const renderChatTesterModal = () => {
    const tokenQuotaText = chatToken?.unlimited_quota
      ? t('token.table.unlimited')
      : renderQuota(chatToken?.remain_quota || 0, t, 2);
    const tokenExhausted =
      !!chatToken && !chatToken.unlimited_quota && chatToken.remain_quota <= 0;

    return (
      <Modal
        open={!!chatToken}
        onClose={() => {
          if (!chatLoading) setChatToken(null);
        }}
        size='large'
        closeIcon={!chatLoading}
      >
        <Modal.Header>令牌聊天测试</Modal.Header>
        <Modal.Content scrolling>
        {chatToken && (
          <Message warning={tokenExhausted} info={!tokenExhausted}>
            <div>
              当前令牌：{chatToken.name || t('token.table.no_name')}，剩余额度：
              {tokenQuotaText}
            </div>
            {tokenExhausted && (
              <div>
                该令牌剩余额度为 0，真实 API 调用会返回 401。请先编辑令牌增加额度或设为无限额度。
              </div>
            )}
          </Message>
        )}
        <div
          style={{
            background: '#f7f8fa',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            marginBottom: 16,
            minHeight: 300,
            padding: 16,
          }}
        >
          {chatForm.systemPrompt && (
            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 12 }}>
              System: {chatForm.systemPrompt}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div
              style={{
                background: '#1678c2',
                borderRadius: 8,
                color: '#fff',
                lineHeight: 1.6,
                maxWidth: '76%',
                padding: '10px 12px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {chatForm.userPrompt || '请输入一条测试消息'}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              marginTop: 14,
            }}
          >
            <div
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                color: chatResult?.error ? '#b91c1c' : '#111827',
                lineHeight: 1.6,
                maxWidth: '76%',
                minHeight: 42,
                padding: '10px 12px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {chatLoading
                ? '正在请求真实 API...'
                : chatResult?.error
                  ? `调用失败：${chatResult.error}`
                  : chatResult?.assistantText || '点击“发送”后在这里查看模型回复'}
            </div>
          </div>
        </div>

        <Form>
          <Form.TextArea
            label='消息'
            value={chatForm.userPrompt}
            rows={3}
            onChange={(e, { value }) =>
              setChatForm((current) => ({ ...current, userPrompt: value }))
            }
          />
          <Form.Group widths='equal'>
            <Form.Input
              label='模型'
              value={chatForm.model}
              placeholder='例如 deepseek-ai/DeepSeek-V4-Pro'
              onChange={(e, { value }) =>
                setChatForm((current) => ({ ...current, model: value }))
              }
            />
            <Form.Input
              label='max_tokens'
              type='number'
              min='1'
              value={chatForm.maxTokens}
              onChange={(e, { value }) =>
                setChatForm((current) => ({ ...current, maxTokens: value }))
              }
            />
            <Form.Input
              label='temperature'
              type='number'
              min='0'
              max='2'
              step='0.1'
              value={chatForm.temperature}
              onChange={(e, { value }) =>
                setChatForm((current) => ({ ...current, temperature: value }))
              }
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              label='系统提示词'
              value={chatForm.systemPrompt}
              onChange={(e, { value }) =>
                setChatForm((current) => ({ ...current, systemPrompt: value }))
              }
            />
            <Form.Checkbox
              label='流式响应'
              checked={chatForm.stream}
              style={{ paddingTop: 28 }}
              onChange={(e, { checked }) =>
                setChatForm((current) => ({ ...current, stream: checked }))
              }
            />
          </Form.Group>
        </Form>

        {chatResult && (
          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              调试信息：{chatResult.status || '-'}，{chatResult.durationMs} ms
            </summary>
            <Message
              error={!!chatResult.error}
              success={!chatResult.error && !!chatResult.status}
            >
              <div>请求地址：{getApiBaseURL()}/chat/completions</div>
              <div>HTTP 状态：{chatResult.status || '-'}</div>
              <div>耗时：{chatResult.durationMs} ms</div>
              {chatResult.error && <div>错误：{chatResult.error}</div>}
            </Message>
            <h4>请求 JSON</h4>
            {renderCopyButton(JSON.stringify(chatResult.requestBody, null, 2))}
            {renderCodeBlock(JSON.stringify(chatResult.requestBody, null, 2))}
            <h4>原始响应</h4>
            {chatResult.responseText && renderCopyButton(chatResult.responseText)}
            {chatResult.responseText
              ? renderCodeBlock(chatResult.responseText)
              : renderCodeBlock('暂无响应内容')}
          </details>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setChatToken(null)} disabled={chatLoading}>
          关闭
        </Button>
        <Button
          primary
          loading={chatLoading}
          disabled={!chatForm.model.trim() || !chatForm.userPrompt.trim()}
          onClick={runChatTest}
        >
          发送测试
        </Button>
      </Modal.Actions>
    </Modal>
    );
  };

  return (
    <>
      {renderUsageModal()}
      {renderChatTesterModal()}
      <Form onSubmit={searchTokens}>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder={t('token.search')}
          value={searchKeyword}
          loading={searching}
          onChange={handleKeywordChange}
        />
      </Form>

      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortToken('name');
              }}
            >
              {t('token.table.name')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortToken('status');
              }}
            >
              {t('token.table.status')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortToken('used_quota');
              }}
            >
              {t('token.table.used_quota')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortToken('remain_quota');
              }}
            >
              {t('token.table.remain_quota')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortToken('created_time');
              }}
            >
              {t('token.table.created_time')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortToken('expired_time');
              }}
            >
              {t('token.table.expired_time')}
            </Table.HeaderCell>
            <Table.HeaderCell>{t('token.table.actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {tokens
            .slice(
              (activePage - 1) * ITEMS_PER_PAGE,
              activePage * ITEMS_PER_PAGE
            )
            .map((token, idx) => {
              if (token.deleted) return <></>;

              const copyOptionsWithHandlers = COPY_OPTIONS.map((option) => ({
                ...option,
                onClick: async () => {
                  await onCopy(option.value, token.key);
                },
              }));

              const openLinkOptionsWithHandlers = OPEN_LINK_OPTIONS.map(
                (option) => ({
                  ...option,
                  onClick: async () => {
                    await onOpenLink(option.value, token.key);
                  },
                })
              );

              return (
                <Table.Row key={token.id}>
                  <Table.Cell>
                    {token.name ? token.name : t('token.table.no_name')}
                  </Table.Cell>
                  <Table.Cell>{renderStatus(token.status, t)}</Table.Cell>
                  <Table.Cell>{renderQuota(token.used_quota, t)}</Table.Cell>
                  <Table.Cell>
                    {token.unlimited_quota
                      ? t('token.table.unlimited')
                      : renderQuota(token.remain_quota, t, 2)}
                  </Table.Cell>
                  <Table.Cell>{renderTimestamp(token.created_time)}</Table.Cell>
                  <Table.Cell>
                    {token.expired_time === -1
                      ? t('token.table.never_expire')
                      : renderTimestamp(token.expired_time)}
                  </Table.Cell>
                  <Table.Cell>
                    <div>
                      <Button.Group color='green' size={'tiny'}>
                        <Button
                          size={'tiny'}
                          positive
                          onClick={async () => await onCopy('', token.key)}
                        >
                          {t('token.buttons.copy')}
                        </Button>
                        <Dropdown
                          className='button icon'
                          floating
                          options={copyOptionsWithHandlers}
                          trigger={<></>}
                        />
                      </Button.Group>{' '}
                      <Button.Group color='olive' size={'tiny'}>
                        <Button
                          size={'tiny'}
                          positive
                          onClick={() => openChatTester(token)}
                        >
                          {t('token.buttons.chat')}
                        </Button>
                        <Dropdown
                          className='button icon'
                          floating
                          options={openLinkOptionsWithHandlers}
                          trigger={<></>}
                        />
                      </Button.Group>{' '}
                      <Button
                        size={'tiny'}
                        color='blue'
                        onClick={() => setUsageToken(token)}
                      >
                        使用方法
                      </Button>{' '}
                      <Popup
                        trigger={
                          <Button size='mini' negative>
                            {t('token.buttons.delete')}
                          </Button>
                        }
                        on='click'
                        flowing
                        hoverable
                      >
                        <Button
                          size={'tiny'}
                          negative
                          onClick={() => {
                            manageToken(token.id, 'delete', idx);
                          }}
                        >
                          {t('token.buttons.confirm_delete')} {token.name}
                        </Button>
                      </Popup>
                      <Button
                        size={'tiny'}
                        onClick={() => {
                          manageToken(
                            token.id,
                            token.status === 1 ? 'disable' : 'enable',
                            idx
                          );
                        }}
                      >
                        {token.status === 1
                          ? t('token.buttons.disable')
                          : t('token.buttons.enable')}
                      </Button>
                      <Button
                        size={'tiny'}
                        as={Link}
                        to={'/token/edit/' + token.id}
                      >
                        {t('token.buttons.edit')}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='7'>
              <Button size='small' as={Link} to='/token/add' loading={loading}>
                {t('token.buttons.add')}
              </Button>
              <Button size='small' onClick={refresh} loading={loading}>
                {t('token.buttons.refresh')}
              </Button>
              <Dropdown
                placeholder={t('token.sort.placeholder')}
                selection
                options={[
                  { key: '', text: t('token.sort.default'), value: '' },
                  {
                    key: 'remain_quota',
                    text: t('token.sort.by_remain'),
                    value: 'remain_quota',
                  },
                  {
                    key: 'used_quota',
                    text: t('token.sort.by_used'),
                    value: 'used_quota',
                  },
                ]}
                value={orderBy}
                onChange={handleOrderByChange}
                style={{ marginLeft: '10px' }}
              />
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={
                  Math.ceil(tokens.length / ITEMS_PER_PAGE) +
                  (tokens.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                }
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default TokensTable;
